import { LoginState } from '@propelauth/frontend-apis';
import { useAuthFrontendApis } from '@propelauth/frontend-apis-react';
import { useAuthInfo, useLogoutFunction } from '@propelauth/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { authApi } from '@/app/services/auth-api.service';
import { Spinner } from '@/components/ui/spinner';
import type { AuthView, InvitationData } from '@/shared/domain/types/auth.types';

import { AuthLayout } from './auth-layout';
import CompleteProfileForm from './complete-profile-form';
import { ContactAdminMessage } from './contact-admin-message';
import { DomainChecker } from './domain-checker';
import { EmailEntryForm } from './email-entry-form';
import { EmailVerificationRequiredMessage } from './email-verification-required-message';
import { InvitationAcceptance } from './invitation-acceptance';
import { InvitationDetails } from './invitation-details';
import { LoginForm } from './login-form';
import { OrgAssignmentHandler } from './org-assignment-handler';
import { PasswordResetForm } from './password-reset-form';
import { SignupForm } from './signup-form';
import { SSOLoginForm } from './sso-login-form';
import { SSORequiredMessage } from './sso-required-message';

interface AuthenticationControllerProps {
  defaultView?: AuthView;
}

export function AuthenticationController({
  defaultView = 'email-entry',
}: AuthenticationControllerProps) {
  const [currentView, setCurrentView] = useState<AuthView>(defaultView);
  const [email, setEmail] = useState('');
  const [orgName, setOrgName] = useState<string | null>(null);
  const [ssoProvider, setSsoProvider] = useState<string | null>(null);
  const [invitationData, setInvitationData] = useState<InvitationData | null>(null);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgAssignmentTimeout, setOrgAssignmentTimeout] = useState(false);
  const [hasNavigatedToSuccess, setHasNavigatedToSuccess] = useState(false);
  const [pollAttempts, setPollAttempts] = useState(0);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    isLoggedIn,
    user,
    loading: authLoading,
    accessToken,
    tokens,
    refreshAuthInfo,
  } = useAuthInfo();
  const { fetchLoginState } = useAuthFrontendApis();
  const logout = useLogoutFunction();

  useEffect(() => {
    if (!authLoading && !hasNavigatedToSuccess) {
      determineAuthFlow();
    }
  }, [authLoading, isLoggedIn, user, hasNavigatedToSuccess]);

  // Clear polling state when leaving org-assignment view
  useEffect(() => {
    if (currentView !== 'org-assignment') {
      setPollAttempts(0);
      setOrgAssignmentTimeout(false);
      if (error && currentView !== 'contact-admin') {
        setError(null);
      }
    }
  }, [currentView, error]);

  // Handle org creation required state - wait for backend webhook to assign user
  useEffect(() => {
    const handleOrgCreationRequired = async () => {
      if (currentView !== 'org-assignment') return;

      if (authLoading) {
        console.log('Auth still loading, waiting...');
        return;
      }

      if (isLoggedIn && !accessToken) {
        return;
      }

      console.log('OrgCreationRequired - waiting for backend webhook to assign user to org...');

      // Clear any previous error when starting fresh
      setError(null);
      setPollAttempts(0);

      const timeoutId = setTimeout(() => {
        setOrgAssignmentTimeout(true);
      }, 15000);

      const MAX_POLL_ATTEMPTS = 30;
      const BASE_DELAY = 2000;
      let currentAttempts = 0;

      const pollLoginState = async () => {
        try {
          currentAttempts++;
          setPollAttempts(currentAttempts);

          if (currentAttempts > MAX_POLL_ATTEMPTS) {
            console.error('Max polling attempts reached. Stopping org assignment polling.');
            clearTimeout(timeoutId);
            setOrgAssignmentTimeout(true);
            setError(
              'Organization assignment is taking longer than expected. Please contact support or try logging out and back in.',
            );
            return;
          }

          console.log(`Org assignment poll attempt ${currentAttempts}/${MAX_POLL_ATTEMPTS}`);

          await refreshAuthInfo();
          const loginState = await checkLoginState();

          if (loginState.type === 'success') {
            console.log('User successfully assigned to org by backend webhook');
            clearTimeout(timeoutId);
            setOrgAssignmentTimeout(false);
            setPollAttempts(0);
            setError(null);
            setHasNavigatedToSuccess(true);
            navigate('/auth/success');
          } else if (loginState.type === 'org_creation_required') {
            const delay = Math.min(
              BASE_DELAY * Math.pow(1.5, Math.floor(currentAttempts / 5)),
              8000,
            );
            console.log(`Still waiting for org assignment, retrying in ${delay}ms...`);
            setTimeout(pollLoginState, delay);
          } else {
            console.error('Unexpected login state during org assignment:', loginState);
            clearTimeout(timeoutId);
            setCurrentView('contact-admin');
          }
        } catch (error) {
          console.error('Error polling login state:', error);

          if (currentAttempts >= MAX_POLL_ATTEMPTS) {
            console.error('Max polling attempts reached after error. Stopping.');
            clearTimeout(timeoutId);
            setOrgAssignmentTimeout(true);
            setError('Unable to complete organization assignment. Please try again later.');
            return;
          }

          const delay = Math.min(BASE_DELAY * Math.pow(2, Math.floor(currentAttempts / 3)), 10000);
          console.log(`Retrying after error in ${delay}ms...`);
          setTimeout(pollLoginState, delay);
        }
      };

      setTimeout(pollLoginState, 1000);

      return () => {
        clearTimeout(timeoutId);
        setOrgAssignmentTimeout(false);
        setPollAttempts(0);
        setError(null);
      };
    };

    handleOrgCreationRequired();
  }, [currentView, authLoading, isLoggedIn, accessToken, navigate]);

  const checkLoginState = async (): Promise<
    | { type: 'success'; state: any }
    | { type: 'needs_auth' }
    | { type: 'error' }
    | { type: 'metadata_required' }
    | { type: 'org_creation_required' }
    | { type: 'email_verification_required' }
  > => {
    try {
      const loginState = await fetchLoginState();

      return await new Promise(resolve => {
        loginState.handle({
          success: ({ login_state }) => {
            if (login_state === LoginState.USER_MISSING_REQUIRED_PROPERTIES) {
              resolve({ type: 'metadata_required' });
            } else if (login_state === LoginState.LOGGED_IN) {
              resolve({ type: 'success', state: login_state });
            } else if (login_state === 'OrgCreationRequired') {
              resolve({ type: 'org_creation_required' });
            } else if (login_state === LoginState.EMAIL_NOT_CONFIRMED_YET) {
              resolve({ type: 'email_verification_required' });
            } else {
              resolve({ type: 'needs_auth' });
            }
          },
          unexpectedOrUnhandled: () => {
            resolve({ type: 'needs_auth' });
          },
        });
      });
    } catch (error) {
      console.error('Failed to check login state:', error);
      return { type: 'error' };
    }
  };

  const determineAuthFlow = async () => {
    const invitationToken = searchParams.get('token');

    if (window.location.pathname === '/auth/invite' && invitationToken) {
      await handleInvitationFlow(invitationToken);
      return;
    }

    const loginState = await checkLoginState();

    if (loginState.type === 'success' && isLoggedIn && user) {
      setHasNavigatedToSuccess(true);
      navigate('/auth/success');
      return;
    }

    if (loginState.type === 'metadata_required') {
      setCurrentView('complete-profile');
      return;
    }

    if (loginState.type === 'org_creation_required') {
      setCurrentView('org-assignment');
      return;
    }

    if (loginState.type === 'email_verification_required') {
      setCurrentView('email-verification-required');
      return;
    }

    if (defaultView === 'login' || defaultView === 'signup') {
      setCurrentView('email-entry');
      return;
    }

    setCurrentView(defaultView);
  };

  const handleInvitationFlow = async (token: string) => {
    try {
      setCurrentView('invitation-details');
      setIsLoading(true);

      const invitation = await authApi.validateInvitation(token);

      if (!invitation.valid) {
        setInvitationError(invitation.error || 'Invalid invitation');
        return;
      }

      setInvitationData({
        token,
        email: invitation.email!,
        orgId: invitation.orgId!,
        orgName: invitation.orgName!,
        role: invitation.role!,
        inviterName: invitation.inviterName!,
        expiresAt: invitation.expiresAt!,
      });

      const loginState = await checkLoginState();

      if (loginState.type === 'success' && isLoggedIn && user) {
        await acceptInvitation(token, user.userId);
      } else if (loginState.type === 'metadata_required') {
        setCurrentView('complete-profile');
      }
    } catch (error) {
      console.error('Invitation validation failed:', error);
      setInvitationError('Failed to validate invitation');
    } finally {
      setIsLoading(false);
    }
  };

  const acceptInvitation = async (token: string, userId: string) => {
    try {
      setCurrentView('invitation-acceptance');
      setIsLoading(true);

      const result = await authApi.acceptInvitation(token, userId);

      if (result.success) {
        console.log(`Invitation accepted! Added to org ${result.orgId} as ${result.role}`);
        setHasNavigatedToSuccess(true);
        navigate('/auth/success');
      } else {
        setInvitationError(result.error || 'Failed to accept invitation');
      }
    } catch (error) {
      console.error('Invitation acceptance failed:', error);
      setInvitationError('Failed to accept invitation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailEntry = async (enteredEmail: string) => {
    try {
      setEmail(enteredEmail);
      setCurrentView('domain-check');
      setError(null);

      const domainResult = await authApi.checkDomain(enteredEmail);

      if (!domainResult.hasOrg) {
        setCurrentView('contact-admin');
        return;
      }

      setOrgName(domainResult.orgName || null);
      setSsoProvider(domainResult.ssoProvider || null);

      if (domainResult.requiresSSO) {
        setCurrentView('sso-required');
        return;
      }

      if (domainResult.ssoProvider) {
        setCurrentView('sso-login');
        return;
      }

      const loginState = await checkLoginState();

      if (loginState.type === 'success') {
        if (defaultView === 'signup') {
          setCurrentView('signup-form');
        } else {
          setCurrentView('login-form');
        }
      } else if (loginState.type === 'metadata_required') {
        setCurrentView('complete-profile');
      } else {
        if (domainResult.hasUser === false) {
          setCurrentView('signup-form');
        } else if (defaultView === 'signup') {
          setCurrentView('signup-form');
        } else {
          setCurrentView('login-form');
        }
      }
    } catch (error) {
      console.error('Domain check failed:', error);
      setCurrentView('contact-admin');
    }
  };

  const handleSSOLogin = (provider: string) => {
    const normalizedProvider = provider.toLowerCase();
    const authUrl = import.meta.env['VITE_AUTH_URL'];
    const userEmail = encodeURIComponent(email);

    if (normalizedProvider === 'saml') {
      window.location.href = `${authUrl}/api/fe/v3/login/saml?email=${userEmail}`;
    } else {
      window.location.href = `${authUrl}/${normalizedProvider}/login?external_param_login_hint=${userEmail}`;
    }
  };

  const handleUseRegularLogin = () => {
    setCurrentView('login-form');
  };

  const handleBackToEmail = () => {
    setCurrentView('email-entry');
    setEmail('');
    setOrgName(null);
    setSsoProvider(null);
  };

  const handleAuthSuccess = async (user: any) => {
    try {
      console.log('handleAuthSuccess called with user:', user);

      // If user data includes loginState, use that; otherwise check login state
      const loginState = user?.loginState
        ? { type: user.loginState === 'OrgCreationRequired' ? 'org_creation_required' : 'success' }
        : await checkLoginState();

      console.log('Determined loginState:', loginState);
      console.log('User loginState:', user?.loginState);

      if (
        loginState.type === 'metadata_required' ||
        user?.loginState === 'USER_MISSING_REQUIRED_PROPERTIES'
      ) {
        setCurrentView('complete-profile');
        return;
      }

      if (loginState.type === 'error') {
        console.error('Login state check failed after auth success');
        setCurrentView('contact-admin');
        return;
      }

      setCurrentView('org-assignment');

      if (invitationData) {
        console.log('Has invitation data, accepting invitation...');
        await acceptInvitation(invitationData.token, user?.userId || '');
        return;
      }

      // For org_creation_required state, assign to org
      if (
        loginState.type === 'org_creation_required' ||
        user?.loginState === 'OrgCreationRequired'
      ) {
        console.log('Org creation required, setting org-assignment view');
        setCurrentView('org-assignment');
        return;
      }

      // If user is already logged in, go to app
      if (loginState.type === 'success' || user?.loginState === 'LOGGED_IN') {
        console.log('User already logged in, navigating to app...');
        setHasNavigatedToSuccess(true);
        navigate('/auth/success');
        return;
      }

      // Fallback to contact admin if we reach here
      console.error('Unexpected state in handleAuthSuccess');
      setCurrentView('contact-admin');
    } catch (error) {
      console.error('Post-auth org assignment failed:', error);
      setCurrentView('contact-admin');
    }
  };

  const getViewConfig = () => {
    switch (currentView) {
      case 'email-entry':
        return {
          title: defaultView === 'signup' ? 'Create your account' : 'Welcome back',
          subtitle:
            defaultView === 'signup'
              ? 'Enter your email to get started'
              : 'Enter your email to sign in',
        };
      case 'domain-check':
        return {
          title: 'Checking domain...',
          subtitle: 'Verifying your organization access',
        };
      case 'login-form':
        return {
          title: 'Welcome back',
          subtitle: 'Sign in to your account',
        };
      case 'signup-form':
        return {
          title: 'Create your account',
          subtitle: 'Complete your registration',
        };
      case 'contact-admin':
        return {
          title: 'Domain Not Found',
          subtitle: 'Your email domain is not registered with Revi',
        };
      case 'sso-login':
        return {
          title: 'SSO Login',
          subtitle: 'Sign in with Single Sign-On',
        };
      case 'sso-required':
        return {
          title: 'SSO Required',
          subtitle: 'Your organization uses Single Sign-On',
        };
      case 'org-assignment':
        return {
          title: 'Setting up your account...',
          subtitle: 'Assigning you to your organization',
        };
      case 'invitation-details':
        return {
          title: "You're Invited!",
          subtitle: invitationData ? `Join ${invitationData.orgName}` : 'Join organization',
        };
      case 'invitation-acceptance':
        return {
          title: 'Accepting invitation...',
          subtitle: 'Adding you to the organization',
        };
      case 'password-reset':
        return {
          title: 'Reset Your Password',
          subtitle: 'Enter your email to reset your password',
        };
      case 'complete-profile':
        return {
          title: 'Complete Your Profile',
          subtitle: 'Add your details to complete your registration',
        };
      case 'email-verification-required':
        return {
          title: 'Verify Your Email',
          subtitle: 'Please check your inbox to verify your email address',
        };
      default:
        return {
          title: 'Welcome',
          subtitle: undefined,
        };
    }
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'email-entry':
        return (
          <EmailEntryForm
            onSubmit={handleEmailEntry}
            defaultEmail={invitationData?.email}
            isLoading={isLoading}
            error={error}
          />
        );
      case 'domain-check':
        return <DomainChecker email={email} />;
      case 'login-form':
        return (
          <LoginForm
            email={email}
            onSuccess={handleAuthSuccess}
            onSwitchToSignup={() => setCurrentView('signup-form')}
            onForgotPassword={() => setCurrentView('password-reset')}
          />
        );
      case 'signup-form':
        return (
          <SignupForm
            email={email}
            onSuccess={handleAuthSuccess}
            onSwitchToLogin={() => setCurrentView('login-form')}
          />
        );
      case 'password-reset':
        return (
          <PasswordResetForm email={email} onBackToLogin={() => setCurrentView('login-form')} />
        );
      case 'contact-admin':
        return <ContactAdminMessage />;
      case 'sso-login':
        return (
          <SSOLoginForm
            email={email}
            ssoProvider={ssoProvider || undefined}
            orgName={orgName || undefined}
            onBackToEmail={handleBackToEmail}
            onSSOLogin={handleSSOLogin}
            onUseRegularLogin={handleUseRegularLogin}
          />
        );
      case 'org-assignment':
        return (
          <OrgAssignmentHandler
            showTimeout={orgAssignmentTimeout}
            pollAttempts={pollAttempts}
            maxAttempts={30}
            error={error}
          />
        );
      case 'invitation-details':
        return invitationData ? (
          <InvitationDetails
            invitation={invitationData}
            onAccept={() => acceptInvitation(invitationData.token, user?.userId || '')}
            onSignup={() => setCurrentView('signup-form')}
            onLogin={() => setCurrentView('login-form')}
            error={invitationError}
          />
        ) : null;
      case 'invitation-acceptance':
        return <InvitationAcceptance />;
      case 'sso-required':
        return (
          <SSORequiredMessage
            email={email}
            orgName={orgName || undefined}
            ssoProvider={ssoProvider || undefined}
            onSSOLogin={handleSSOLogin}
            onBackToEmail={handleBackToEmail}
          />
        );
      case 'complete-profile':
        return (
          <CompleteProfileForm
            onSuccess={() => {
              if (invitationData && user) {
                acceptInvitation(invitationData.token, user.userId);
              } else {
                // Redirect to success, or handle next step if needed after profile completion
                // For example, if email verification is now required AFTER profile completion,
                // you might want to re-check login state or navigate accordingly.
                // For now, assuming success navigation is appropriate.
                window.location.href = '/auth/success';
              }
            }}
          />
        );
      case 'email-verification-required':
        return <EmailVerificationRequiredMessage email={email} />;
      default:
        return (
          <div className="py-8 text-center">
            <Spinner size={24} className="mx-auto mb-4 text-black" />
            <p className="mt-4 text-neutral-600">Loading...</p>
          </div>
        );
    }
  };

  if (isLoading && currentView === 'invitation-details') {
    return (
      <AuthLayout>
        <div className="py-8 text-center">
          <Spinner size={24} className="mx-auto mb-4 text-black" />
          <p className="mt-4 text-neutral-600">Validating invitation...</p>
        </div>
      </AuthLayout>
    );
  }

  const { title, subtitle } = getViewConfig();

  return (
    <AuthLayout title={title} subtitle={subtitle}>
      <AnimatePresence mode="wait">
        <motion.div
          key={currentView}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {renderCurrentView()}
        </motion.div>
      </AnimatePresence>
    </AuthLayout>
  );
}
